import { useEffect, useMemo, useState } from 'react';

import { formatDateOnly } from '../../../common/utils/date';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { useSgCows } from '../contexts/cows';
import SgCowDetail from './cow-detail';

import '../styles/sg.css';

// Software Ganadero · navegador de vacas físicas (GET /admin/sg/cows · B1). Una fila
// por vaca cowre (SoT), agrupando sus fichas SG. El backend ya filtra a conciliadas +
// activas: aquí NO hay match_state, ni fichas sin conciliar, ni historial de backups.
// Master-detail: al seleccionar una fila se monta el detalle (D-004) al lado, con
// key={cowId} para que su estado (tab/historial) se reinicie al cambiar de vaca; la
// lista se compacta (CSS oculta columnas secundarias).

const LIMIT = 50;  // default del backend; máx 200 → 400.

const SEX_LABEL = { H: 'Hembra', M: 'Macho' };

// last_events es { feature: 'YYYY-MM-DD' }. Etiquetas de feature (whitelist del contrato).
const FEATURE_LABEL = { milk: 'Leche', palpation: 'Palpación', service: 'Servicio', calving: 'Parto', weight: 'Peso', treatment: 'Tratamiento' };

function LastEvents({ events }) {
    const entries = events && typeof events === 'object' ? Object.entries(events).filter(([, d]) => d) : [];
    if (!entries.length) return <span className="sg-cows__empty">—</span>;
    return (
        <span className="sg-cows__chips">
            {entries.map(([feature, date]) => (
                <span key={feature} className="sg-cows__chip">{FEATURE_LABEL[feature] || feature}: {formatDateOnly(date)}</span>
            ))}
        </span>
    );
}

export default function SgCows() {
    const [qInput, setQInput]   = useState('');   // texto crudo del buscador (UI)
    const [q, setQ]             = useState('');    // valor con debounce que va a la query
    const [sgCodfin, setSgCodfin] = useState('');
    const [offset, setOffset]   = useState(0);
    const [selectedCowId, setSelectedCowId] = useState(null);  // animal_id de la fila activa (la usa D-004)

    // Debounce del buscador: no golpear la red en cada tecla. Al cambiar q, volver a la página 1.
    useEffect(() => {
        const t = setTimeout(() => { setQ(qInput.trim()); setOffset(0); }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [qInput]);

    const filters = useMemo(() => {
        const out = { limit: LIMIT, offset };
        if (q) out.q = q;
        if (sgCodfin.trim()) out.sg_codfin = sgCodfin.trim();
        return out;
    }, [q, sgCodfin, offset]);

    const { data, isLoading, isFetching, error } = useSgCows(filters);
    const items = useMemo(() => data?.items ?? [], [data]);
    const pag   = data?.pagination;
    const total = pag?.total ?? items.length;
    const page  = pag?.page ?? 1;
    const pages = pag?.pages ?? 1;

    const hasFilters = !!(q || sgCodfin.trim());
    const clearFilters = () => { setQInput(''); setQ(''); setSgCodfin(''); setOffset(0); };
    const prev = () => setOffset((o) => Math.max(0, o - LIMIT));
    const next = () => setOffset((o) => o + LIMIT);

    return (
        <div className="sg-cows">
            <div className="sg-cows__toolbar">
                <div className="sg-cows__search">
                    <input
                        type="text"
                        value={qInput}
                        onChange={(e) => setQInput(e.target.value)}
                        placeholder="Buscar por número, nombre u hoja…"
                        aria-label="Buscar vaca"
                    />
                </div>
                <input
                    className="sg-cows__filter"
                    type="text"
                    value={sgCodfin}
                    onChange={(e) => { setSgCodfin(e.target.value); setOffset(0); }}
                    placeholder="Finca SG (sg_codfin)"
                    aria-label="Filtrar por finca SG"
                    maxLength={6}
                />
                <span className="sg-cows__count">{total} {total === 1 ? 'vaca' : 'vacas'}</span>
                {hasFilters && <button type="button" className="sg-cows__clear" onClick={clearFilters}>Limpiar</button>}
            </div>

            {error && (
                <div className="sg-cows__error">
                    {error.status === 403 ? 'No tenés permiso para ver las vacas de Software Ganadero.' : (error.message || 'No se pudieron cargar las vacas')}
                </div>
            )}

            {isLoading ? (
                <div className="sg-cows__state">Cargando vacas…</div>
            ) : !items.length ? (
                <div className="sg-cows__state">
                    <p className="sg-cows__state-text">{hasFilters ? 'Ninguna vaca coincide con la búsqueda.' : 'Sin vacas.'}</p>
                </div>
            ) : (
                <div className={`sg-cows__layout ${selectedCowId ? 'sg-cows__layout--split' : ''}`}>
                    <div className="sg-cows__list">
                    <div className={`sg-cows__card ${isFetching ? 'sg-cows__card--busy' : ''}`}>
                        <table className={`sg-cows__table ${selectedCowId ? 'sg-cows__table--compact' : ''}`}>
                            <thead>
                                <tr>
                                    <th>Vaca</th><th>Número</th><th>Registro</th><th>Sexo</th><th>Fichas</th><th>Últimos eventos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr
                                        key={row.animal_id}
                                        className={`sg-cows__row ${selectedCowId === row.animal_id ? 'sg-cows__row--active' : ''}`}
                                        onClick={() => setSelectedCowId(row.animal_id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCowId(row.animal_id); } }}
                                    >
                                        <td>
                                            {row.cow?.name
                                                ? <span className="sg-cows__name">{row.cow.name}</span>
                                                : <span className="sg-cows__num">#{row.cow?.number || row.animal_id}</span>}
                                        </td>
                                        <td>{row.cow?.number ? <span className="sg-cows__num">{row.cow.number}</span> : <span className="sg-cows__empty">—</span>}</td>
                                        <td>{row.cow?.register || <span className="sg-cows__empty">—</span>}</td>
                                        <td>{SEX_LABEL[row.sg?.sex] || row.sg?.sex || <span className="sg-cows__empty">—</span>}</td>
                                        <td>
                                            {row.sg_count > 1
                                                ? <span className="sg-cows__fichas" title={`${row.sg_count} fichas SG de esta vaca`}>{row.sg_count} fichas</span>
                                                : <span className="sg-cows__muted">1</span>}
                                        </td>
                                        <td><LastEvents events={row.last_events} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pages > 1 && (
                        <div className="sg-cows__pager">
                            <button type="button" className="sg-cows__page-btn" onClick={prev} disabled={offset === 0 || isFetching}>Anteriores</button>
                            <span className="sg-cows__page-info">Página {page} de {pages}</span>
                            <button type="button" className="sg-cows__page-btn" onClick={next} disabled={page >= pages || isFetching}>Siguientes</button>
                        </div>
                    )}
                    </div>

                    {selectedCowId != null && (
                        <SgCowDetail key={selectedCowId} cowId={selectedCowId} onClose={() => setSelectedCowId(null)} />
                    )}
                </div>
            )}
        </div>
    );
}
